import React, { useState, useEffect } from 'react';
import { X, CalendarDays, FileText, FileType, FileSpreadsheet, ChevronDown, Loader2 } from 'lucide-react';
import SettlementDownloadButton from './SettlementDownloadButton';
import { getData } from '../../../../services/apiService';

const SettlementModal = ({
  event,
  onClose,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  selectedFormat,
  onFormatChange,
  formatDate
}) => {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(event || null);
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventError, setEventError] = useState('');

  // Fetch all events when component mounts
  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const response = await getData('Event/GetAllEvents');

        if (response && !response.isError && response.data) {
          setEvents(response.data);

          // If no event was passed as prop, don't auto-select anything
          if (!event && response.data.length > 0) {
            setSelectedEvent(null);
          }
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchEvents();
  }, [event]);

  // Filter events based on search term
  const filteredEvents = events.filter(eventItem =>
    eventItem.Event_Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Validate if all fields are filled
  const isFormValid = () => {
    return selectedEvent && fromDate && toDate && selectedFormat;
  };

  const getModalTitle = () => {
    if (selectedEvent) {
      return `Settlement Details - ${selectedEvent.Event_Name}`;
    }
    return "Settlement Details";
  };

  const getModalDescription = () => {
    if (selectedEvent) {
      return `Download settlement report for ${selectedEvent.Event_Name}`;
    }
    return "Please select an event to download settlement report";
  };

  const handleEventSelect = (selectedEventItem) => {
    setSelectedEvent(selectedEventItem);
    setShowEventDropdown(false);
    setSearchTerm('');
    setEventError('');
  };

  const clearEventSelection = () => {
    setSelectedEvent(null);
    setSearchTerm('');
    setEventError('Event selection is required');
  };

  const handleEventDropdownClick = () => {
    setShowEventDropdown(!showEventDropdown);
    if (selectedEvent) {
      setEventError('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{getModalTitle()}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {getModalDescription()}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {/* Event Selection Dropdown - Required */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Select Event
            </h3>

            <div className="relative">
              <div
                className="relative cursor-pointer"
                onClick={handleEventDropdownClick}
              >
                <div className={`w-full px-3 py-2 border rounded-lg bg-white flex items-center justify-between ${showEventDropdown ? 'border-blue-500 ring-2 ring-blue-200' :
                    !selectedEvent && eventError ? 'border-red-300' : 'border-gray-300 hover:border-gray-400'
                  }`}>
                  <div className="flex-1 truncate">
                    {selectedEvent ? (
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {selectedEvent.Event_Name}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            clearEventSelection();
                          }}
                          className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">
                        {loadingEvents ? 'Loading events...' : 'Select an event (Optional)'}
                      </span>
                    )}
                  </div>
                  {loadingEvents ? (
                    <Loader2 size={18} className="text-gray-400 animate-spin" />
                  ) : (
                    <ChevronDown size={18} className={`text-gray-400 transition-transform ${showEventDropdown ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </div>

              {/* Error message for event selection */}
              {!selectedEvent && eventError && (
                <p className="text-xs text-red-500 mt-1">{eventError}</p>
              )}

              {/* Dropdown Menu */}
              {showEventDropdown && !loadingEvents && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {/* Search Input */}
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Events List */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredEvents.length > 0 ? (
                      filteredEvents.map((eventItem) => (
                        <div
                          key={eventItem.Event_Id}
                          onClick={() => handleEventSelect(eventItem)}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${selectedEvent?.Event_Id === eventItem.Event_Id ? 'bg-blue-50' : ''
                            }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center mr-2 ${selectedEvent?.Event_Id === eventItem.Event_Id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                              }`}>
                              {selectedEvent?.Event_Id === eventItem.Event_Id && <div className="w-2 h-2 rounded-full bg-white"></div>}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {eventItem.Event_Name}
                              </p>
                              <p className="text-xs text-gray-500">
                                Event ID: {eventItem.Event_Id}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        No events found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Date Range Selection - Required */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Select Date Range
              <span className="text-red-500 ml-1">*</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  From Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => onFromDateChange(e.target.value)}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!fromDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                />
                {!fromDate && (
                  <p className="text-xs text-red-500 mt-1">From Date is required</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  To Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => onToDateChange(e.target.value)}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!toDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                />
                {!toDate && (
                  <p className="text-xs text-red-500 mt-1">To Date is required</p>
                )}
              </div>
            </div>
            <div className={`mt-3 flex items-center text-sm ${!fromDate || !toDate ? 'text-red-500' : 'text-gray-500'
              }`}>
              <CalendarDays size={14} className="mr-1" />
              {!fromDate || !toDate
                ? "Please select both From Date and To Date"
                : fromDate && toDate
                  ? `Date range: ${fromDate} to ${toDate}`
                  : ""}
            </div>
          </div>

          {/* Format Selection - Required */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Select Format
              <span className="text-red-500 ml-1">*</span>
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => onFormatChange('pdf')}
                className={`p-3 rounded-lg border flex flex-col items-center justify-center transition-all ${selectedFormat === 'pdf'
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <FileText className={`${selectedFormat === 'pdf' ? 'text-blue-600' : 'text-gray-400'}`} size={20} />
                <span className={`text-xs mt-1 ${selectedFormat === 'pdf' ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>PDF</span>
              </button>
              <button
                onClick={() => onFormatChange('csv')}
                className={`p-3 rounded-lg border flex flex-col items-center justify-center transition-all ${selectedFormat === 'csv'
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <FileType className={`${selectedFormat === 'csv' ? 'text-blue-600' : 'text-gray-400'}`} size={20} />
                <span className={`text-xs mt-1 ${selectedFormat === 'csv' ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>CSV</span>
              </button>
              <button
                onClick={() => onFormatChange('excel')}
                className={`p-3 rounded-lg border flex flex-col items-center justify-center transition-all ${selectedFormat === 'excel'
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <FileSpreadsheet className={`${selectedFormat === 'excel' ? 'text-blue-600' : 'text-gray-400'}`} size={20} />
                <span className={`text-xs mt-1 ${selectedFormat === 'excel' ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>Excel</span>
              </button>
            </div>
            {!selectedFormat && (
              <p className="text-xs text-red-500 mt-2">Please select a format</p>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <SettlementDownloadButton
              format={selectedFormat}
              event={selectedEvent}
              fromDate={fromDate}
              toDate={toDate}
              onDownload={onClose}
              disabled={!isFormValid()} // Pass disabled prop to button
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettlementModal;